import { useCallback, useMemo } from 'react';
import type { PostOfficeUserInteractionHandlers } from '@atlassian/post-office-analytics/types';

export type FireAnalyticsEventPayload = {
	eventType: string;
	action?: string;
	actionSubject?: string;
	actionSubjectId?: string;
	attributes?: Record<string, unknown>;
};

export type HandlersParams = {
	fireAnalyticsEvent: (event: FireAnalyticsEventPayload) => void;
	onClosed: PostOfficeUserInteractionHandlers['onClosed'];
	onCtaActioned: PostOfficeUserInteractionHandlers['onCtaActioned'];
	onDismissed: () => void;
	setIsModalOpen: (isOpen: boolean) => void;
};

export const useRovoAwarenessHandlers = ({
	fireAnalyticsEvent,
	onClosed,
	onCtaActioned,
	onDismissed,
	setIsModalOpen,
}: HandlersParams): {
	handleDismiss: () => void;
	handleAskRovoClick: () => void;
	handleMoreIdeasClick: () => void;
} => {
	const handleDismiss = useCallback(() => {
		void onClosed({
			interactionIdentifier: 'dismissBanner',
		});

		fireAnalyticsEvent({
			eventType: 'ui',
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'rovoAwarenessHomeBannerDismiss',
		});

		onDismissed();
	}, [fireAnalyticsEvent, onClosed, onDismissed]);

	const handleAskRovoClick = useCallback(() => {
		void onCtaActioned({
			interactionIdentifier: 'askRovo',
		});

		fireAnalyticsEvent({
			eventType: 'ui',
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'rovoAwarenessHomeBannerAskRovo',
		});

		// Ask Rovo handler - can be expanded as needed
	}, [fireAnalyticsEvent, onCtaActioned]);

	const handleMoreIdeasClick = useCallback(() => {
		void onCtaActioned({
			interactionIdentifier: 'moreIdeas',
		});

		fireAnalyticsEvent({
			eventType: 'ui',
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'rovoAwarenessHomeBannerMoreIdeas',
		});

		setIsModalOpen(true);
	}, [fireAnalyticsEvent, onCtaActioned, setIsModalOpen]);

	return useMemo(
		() => ({
			handleDismiss,
			handleAskRovoClick,
			handleMoreIdeasClick,
		}),
		[handleDismiss, handleAskRovoClick, handleMoreIdeasClick],
	);
};
